import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { TAB_CONSTANTS } from "./constants";
import type { ITabsItem } from "./types";

type UseTabsOverflowProps = {
  items: ITabsItem[];
  tabButtonRefs: RefObject<Map<string, HTMLButtonElement>>;
  scrollRef: RefObject<HTMLDivElement | null>;
};

type UseTabsOverflowResult = {
  lastVisibleIndex: number;
  showMore: boolean;
  hasCalculated: boolean;
};

export function useTabsOverflow({
  items,
  tabButtonRefs,
  scrollRef,
}: UseTabsOverflowProps): UseTabsOverflowResult {
  const [lastVisibleIndex, setLastVisibleIndex] = useState(items.length - 1);
  const [hasCalculated, setHasCalculated] = useState(false);

  const calculateOverflow = useCallback(() => {
    if (items.length === 0) {
      setLastVisibleIndex(items.length - 1);
      return;
    }

    const container = scrollRef.current;
    if (!container) {
      setLastVisibleIndex(items.length - 1);
      return;
    }

    const containerWidth = container.clientWidth;

    // First pass: Check if all tabs fit WITHOUT the "More" button
    let totalWidth = 0;
    let allTabsWidth = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const button = tabButtonRefs.current?.get(item.id);

      if (button) {
        const tabWidth = button.offsetWidth;
        const gapWidth = i > 0 ? TAB_CONSTANTS.GAP_PX : 0;
        allTabsWidth += tabWidth + gapWidth;
      }
    }

    // If all tabs fit without the button, show all tabs
    if (allTabsWidth <= containerWidth) {
      setLastVisibleIndex(items.length - 1);
      setHasCalculated(true);
      return;
    }

    // Second pass: Some tabs don't fit, calculate with "More" button space reserved
    totalWidth = TAB_CONSTANTS.MORE_BUTTON_WIDTH + TAB_CONSTANTS.GAP_PX;
    let calculatedIndex = items.length - 1; // Default to all visible

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const button = tabButtonRefs.current?.get(item.id);

      if (button) {
        const tabWidth = button.offsetWidth;
        const gapWidth = i > 0 ? TAB_CONSTANTS.GAP_PX : 0;
        const widthNeeded = tabWidth + gapWidth;
        const newTotal = totalWidth + widthNeeded;

        if (newTotal <= containerWidth) {
          totalWidth = newTotal;
          calculatedIndex = i;
        } else {
          // This tab doesn't fit, previous one was the last visible
          calculatedIndex = i - 1;
          break;
        }
      }
    }

    // Ensure at least first tab is visible
    if (calculatedIndex < 0 && items.length > 0) {
      calculatedIndex = 0;
    }

    setLastVisibleIndex(calculatedIndex);
    setHasCalculated(true);
  }, [items, tabButtonRefs, scrollRef]);

  // Initial calculation with timeout to ensure DOM is ready
  useLayoutEffect(() => {
    // Use timeout to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      calculateOverflow();
    }, TAB_CONSTANTS.INITIAL_CALC_DELAY);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [calculateOverflow, items.length]);

  // Set up ResizeObserver for container changes
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      calculateOverflow();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateOverflow, scrollRef]);

  const showMore = lastVisibleIndex < items.length - 1;

  return {
    lastVisibleIndex,
    showMore,
    hasCalculated,
  };
}
